import { CaretDownCircle } from '@/components/base/Icons/CaretDownIcon';
import cx from 'classnames';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';

import IconButton from '../IconButton/IconButton';
import styles from './FilterBar.module.css';
import FilterChip, { type FilterChipProps } from '@/storybook/components/FilterChip/FilterChip';
import ShareIcon from '@/storybook/icons/ShareIcon';
import { TestIds } from './testIds';

export interface FilterBarChipProps extends Omit<FilterChipProps, 'size'> {
  id: string;
}

interface FilterBarProps {
  children?: React.ReactNode;
  onClearAllClick?: VoidFunction;
  onSaveFiltersClick?: VoidFunction;
  onShareFiltersClick?: VoidFunction;
  filters: FilterBarChipProps[];
  className?: string;
}

const FilterBar: FC<FilterBarProps> = ({
  filters,
  onClearAllClick,
  onSaveFiltersClick,
  onShareFiltersClick,
  className,
}) => {
  const { t } = useTranslation();
  const [isFilterListFolded, setIsFilterListFolded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const isOverflowing = hasOverflow && !isFilterListFolded;
  const shouldShowFoldButton = isOverflowing || isFilterListFolded;

  const handleClearAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClearAllClick?.();
  };

  const handleSaveFilters = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSaveFiltersClick?.();
  };

  const handleShareFilters = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onShareFiltersClick?.();
  };

  const filtersCount = filters?.length ?? 0;
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const list = listRef.current;
    if (!scrollContainer || !list || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let rafId = 0;

    const measure = () => {
      setHasOverflow(scrollContainer.scrollWidth > scrollContainer.clientWidth);
    };

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    });

    observer.observe(scrollContainer);
    observer.observe(list);
    measure();

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [filtersCount]);

  return (
    <div
      className={cx(styles.filterBarContainer, className, {
        [styles.isFilterListFolded]: isFilterListFolded,
      })}
    >
      <div
        className={cx(styles.filterListContainer, {
          [styles.isFilterListFolded]: isFilterListFolded,
        })}
        data-testid={TestIds.scrollContainer}
        ref={scrollContainerRef}
      >
        <span
          className={cx(styles.caretShadow, styles.left, {
            [styles.caretShadowVisible]: isFilterListFolded || !isOverflowing,
          })}
        />

        <ul ref={listRef}>
          {filters?.map(({ id, ...chipProps }) => (
            <li key={id} className={styles.filterListItem}>
              <FilterChip {...chipProps} size="S" />
            </li>
          ))}
          {filters?.length ? (
            <li key="clear-all" className={styles.filterListItem}>
              <Button variant="text" size="S" onClick={handleClearAll}>
                {t('global.filters.clearAll')}
              </Button>
            </li>
          ) : null}
        </ul>
      </div>

      <div className={styles.rightPanelWrapper}>
        <span
          className={cx(styles.caretShadow, styles.right, {
            [styles.caretShadowVisible]: isFilterListFolded || !isOverflowing,
          })}
        />

        <div className={styles.actionButtonsContainer}>
          {shouldShowFoldButton && (
            <IconButton
              className={cx(styles.caretIcon, {
                [styles.caretIconRotated]: isFilterListFolded,
              })}
              appearance="text"
              size="M"
              aria-expanded={isFilterListFolded}
              aria-label={
                isFilterListFolded ? t('global.filters.showFewerFilters') : t('global.filters.showAllFilters')
              }
              icon={<CaretDownCircle />}
              onClick={() => setIsFilterListFolded(prev => !prev)}
            />
          )}

          <Button variant="text" size="S" onClick={handleSaveFilters}>
            {t('global.filters.saveFilters')}
          </Button>

          <Button variant="text" size="S" startIcon={<ShareIcon />} onClick={handleShareFilters}>
            {t('global.filters.share')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
